
import { useState } from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const TrustProbateForm = ({
  formId,
  title,
  subtitle,
}: {
  formId: string;
  title: string;
  subtitle: string;
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    situation: '',
    timeline: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('propertyAddress', formData.propertyAddress);
      formBody.append('situation', formData.situation);
      formBody.append('timeline', formData.timeline);
      formBody.append('message', formData.message);

      await fetch('https://readdy.ai/api/form/d679qplt1k61917cla8g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
          <i className="ri-check-line text-3xl text-teal-600"></i>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600">
          We&apos;ve received your information and will contact you within 24
          hours to discuss your property situation.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-100">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600">{subtitle}</p>
      </div>

      <form
        id={formId}
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number *
            </label>
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="(915) 555-0123"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="your@email.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Property Address
          </label>
          <input
            type="text"
            name="propertyAddress"
            value={formData.propertyAddress}
            onChange={(e) =>
              setFormData({ ...formData, propertyAddress: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="Address of the property"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Your Situation
            </label>
            <select
              name="situation"
              value={formData.situation}
              onChange={(e) =>
                setFormData({ ...formData, situation: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select situation</option>
              <option value="Planning Estate">Planning my estate</option>
              <option value="Inherited Property Trust">Inherited property in a trust</option>
              <option value="Inherited Property Probate">Inherited property going through probate</option>
              <option value="Considering Trust">Considering setting up a trust</option>
              <option value="Selling Trust Property">Need to sell property in a trust</option>
              <option value="Selling Probate Property">Need to sell property in probate</option>
              <option value="Multiple Heirs">Multiple heirs involved</option>
              <option value="Other">Other situation</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timeline
            </label>
            <select
              name="timeline"
              value={formData.timeline}
              onChange={(e) =>
                setFormData({ ...formData, timeline: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select timeline</option>
              <option value="ASAP">Need help ASAP</option>
              <option value="1-3 months">Within 1-3 months</option>
              <option value="3-6 months">Within 3-6 months</option>
              <option value="6+ months">6+ months</option>
              <option value="Just exploring">Just exploring options</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tell Us More
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={(e) =>
              setFormData({
                ...formData,
                message: e.target.value.slice(0, 500),
              })
            }
            rows={3}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
            placeholder="Tell us about your situation and how we can help..."
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.message.length}/500 characters
          </p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Submitting...
            </span>
          ) : (
            'Get Your Free Consultation'
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          Your information is secure and will never be sold. We&apos;re here to
          help.
        </p>
      </form>
    </div>
  );
};

export default function TrustVsProbatePage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Trust vs Probate: Which is Better for El Paso Texas Homeowners | TREX RE LLC"
        description="Comprehensive guide comparing trusts and probate for El Paso homeowners. Learn the costs, timelines, privacy benefits, and which estate planning option is right for your Texas property and family situation."
        keywords="trust vs probate Texas, living trust El Paso, avoid probate Texas, revocable trust Texas, estate planning El Paso, Texas probate costs, trust benefits homeowners, El Paso estate planning"
        canonical="/elpaso-tx/trust-vs-probate"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Trust vs Probate: Which is Better for El Paso Texas Homeowners?',
          description:
            'Comprehensive guide comparing trusts and probate for Texas homeowners, including costs, timelines, and benefits of each approach.',
          author: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/3ca4c82f78e4e3465aa5202e7b990c64.jpg)`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-500/90 text-white text-sm font-semibold rounded-full mb-6">
                <i className="ri-shield-check-line mr-2"></i>
                Estate Planning Guide
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Trust vs Probate: <span className="text-teal-400">Which is Better</span> for El Paso Homeowners?
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                Discover the key differences between trusts and probate, understand the costs and benefits of each, and learn which option best protects your El Paso property and your family&apos;s future.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center text-white">
                  <i className="ri-checkbox-circle-line text-teal-400 mr-2"></i>
                  <span>Avoid Probate Delays</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-checkbox-circle-line text-teal-400 mr-2"></i>
                  <span>Protect Privacy</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-checkbox-circle-line text-teal-400 mr-2"></i>
                  <span>Save on Costs</span>
                </div>
              </div>
            </div>

            <div>
              <TrustProbateForm
                formId="hero-trust-probate-form"
                title="Need Help With Estate Property?"
                subtitle="We buy properties from trusts and probate estates"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you own a home in El Paso, Texas, you&apos;ve probably heard conflicting advice about estate planning. Some people swear by living trusts as the ultimate way to protect your family. Others say a simple will is all you need. The truth? <strong>The right choice depends entirely on your specific situation</strong> — your assets, your family dynamics, your goals, and yes, even your budget.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            This comprehensive guide will walk you through everything El Paso homeowners need to know about trusts versus probate. We&apos;ll compare costs, timelines, privacy considerations, and the practical implications of each approach. By the end, you&apos;ll have a clear understanding of which option makes the most sense for your family and your property.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Whether you&apos;re planning ahead for your own estate or you&apos;ve recently inherited property and are trying to understand your options, this guide will give you the knowledge you need to make informed decisions. And if you&apos;re dealing with{' '}
            <a
              href="/inherited-homes"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              inherited homes
            </a>{' '}
            that need to be sold quickly, we&apos;ll explain how both trusts and probate affect that process.
          </p>
        </div>

        {/* Understanding the Basics Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Understanding the Basics: What Are Trusts and Probate?
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=split%20image%20showing%20legal%20trust%20document%20on%20one%20side%20and%20probate%20court%20gavel%20on%20other%20side%20representing%20two%20different%20estate%20planning%20paths%20professional%20legal%20photography%20warm%20lighting%20clean%20modern%20design&width=800&height=500&seq=trust-probate-basics-002&orientation=landscape"
            alt="Trust document versus probate court comparison"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Before we dive into comparisons, let&apos;s make sure we&apos;re speaking the same language. These terms get thrown around a lot, but many people don&apos;t fully understand what they mean.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            What Is Probate?
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Probate is the legal process of settling a deceased person&apos;s estate. When someone dies with a will, probate validates that will and ensures the deceased&apos;s wishes are carried out. When someone dies without a will (intestate), probate determines who inherits according to state law.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            During probate, a court-appointed executor (or administrator, if there&apos;s no will) takes inventory of assets, pays debts and taxes, and distributes remaining property to beneficiaries. The process involves court filings, potential hearings, and public records. For a detailed breakdown of the Texas probate process, see our{' '}
            <a
              href="/elpaso-tx/texas-probate-guide"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              comprehensive Texas probate guide
            </a>.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            What Is a Trust?
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            A trust is a legal arrangement where one person (the grantor or settlor) transfers assets to another person or entity (the trustee) to hold and manage for the benefit of designated beneficiaries. Think of it as a legal container that holds your assets according to rules you create.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The most common type for estate planning is a <strong>revocable living trust</strong>. With this arrangement, you typically serve as your own trustee during your lifetime, maintaining complete control over your assets. You can change the trust, add or remove assets, or even dissolve it entirely. When you pass away, a successor trustee you&apos;ve named takes over and distributes assets according to your instructions — without going through probate.
          </p>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-lightbulb-line mr-2 text-teal-600"></i>
              Key Distinction
            </h4>
            <p className="text-gray-700">
              A will tells the court what you want to happen with your assets after you die. A trust actually transfers ownership of assets during your lifetime, so there&apos;s nothing for the court to oversee when you pass away. This is the fundamental reason trusts avoid probate.
            </p>
          </div>
        </section>

        {/* The Case for Trusts Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            The Case for Trusts: Why Many El Paso Homeowners Choose This Route
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Living trusts have become increasingly popular among Texas homeowners, and for good reason. Here are the primary advantages that make trusts attractive for estate planning:
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=happy%20multigenerational%20family%20in%20beautiful%20southwestern%20home%20discussing%20estate%20planning%20documents%20together%20warm%20natural%20lighting%20professional%20photography%20showing%20family%20protection%20and%20wealth%20transfer%20concept&width=800&height=500&seq=trust-probate-family-003&orientation=landscape"
            alt="Family discussing estate planning with trust documents"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            1. Avoiding Probate Entirely
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The biggest advantage of a properly funded trust is that assets held in the trust don&apos;t go through probate. When you pass away, your successor trustee can immediately begin managing and distributing assets according to your instructions. There&apos;s no waiting for court approval, no filing fees, and no public proceedings.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For El Paso families, this can mean the difference between beneficiaries receiving their inheritance in weeks versus months or even years. If you own property in multiple states, a trust is especially valuable — without one, your family might face probate proceedings in every state where you own real estate.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            2. Privacy Protection
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Probate is a public process. Anyone can go to the courthouse and look up probate records, seeing exactly what assets someone owned and who inherited them. This information becomes part of the permanent public record.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Trusts, by contrast, are private documents. The terms of your trust, the assets it holds, and who receives what remain confidential. For families who value privacy — or who want to protect beneficiaries from unwanted attention — this can be a significant benefit.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            3. Incapacity Planning
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            A living trust provides seamless management if you become incapacitated. Your successor trustee can step in immediately to manage trust assets, pay bills, and handle financial matters without going to court for a guardianship or conservatorship.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            With only a will, if you become incapacitated, your family may need to petition the court for authority to manage your affairs — a process that can be expensive, time-consuming, and emotionally difficult.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            4. Flexibility and Control
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Trusts offer tremendous flexibility in how and when beneficiaries receive assets. You can:
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Distribute assets at specific ages (e.g., 25, 30, 35)</span>
            </div>
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Tie distributions to milestones (graduation, marriage)</span>
            </div>
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Provide for special needs beneficiaries without affecting benefits</span>
            </div>
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Protect assets from beneficiaries&apos; creditors or divorcing spouses</span>
            </div>
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Include incentive provisions for education or career goals</span>
            </div>
            <div className="flex items-start">
              <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Provide ongoing management for beneficiaries who can&apos;t manage money</span>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            5. Potential Cost Savings Over Time
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            While trusts cost more to set up than wills, they can save money in the long run by avoiding probate costs. In Texas, probate expenses typically include court filing fees, attorney fees, executor fees, and potentially bond premiums. For larger estates, these costs can be substantial.
          </p>
        </section>

        {/* The Case for Probate Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            The Case for Probate: When a Will Might Be Enough
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Despite the advantages of trusts, probate isn&apos;t always the enemy it&apos;s made out to be — especially in Texas. Here&apos;s why some El Paso homeowners choose to rely on a will and accept probate:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            1. Texas Has Relatively Simple Probate
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Unlike some states where probate is notoriously expensive and time-consuming, Texas offers several streamlined probate options. <strong>Independent administration</strong> — available when specified in a will — allows executors to handle most matters without ongoing court supervision. This significantly reduces costs and delays.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For straightforward estates with cooperative beneficiaries, Texas probate can often be completed in 6-12 months with relatively modest legal fees. The horror stories you hear about probate often come from states with much more burdensome requirements.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            2. Lower Upfront Costs
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            A basic will costs significantly less than a comprehensive trust package. While a simple will might cost $300-$1,000, a properly drafted living trust with all supporting documents typically runs $2,000-$5,000 or more, depending on complexity.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For younger homeowners with modest estates, the upfront savings of a will may outweigh the potential probate costs their heirs might face decades in the future.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            3. Simpler to Create and Maintain
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            A will is a relatively simple document. You sign it, have it witnessed, and you&apos;re done. A trust requires more ongoing attention — you must actually transfer assets into the trust (called &quot;funding&quot;), update beneficiary designations, and ensure new assets are properly titled.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Many people create trusts but never properly fund them, leaving their families with the worst of both worlds: they paid for a trust but still face probate because assets weren&apos;t transferred.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            4. Court Oversight Can Be Protective
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            In some situations, court oversight during probate actually protects beneficiaries. If there are concerns about an executor&apos;s honesty or competence, or if family conflicts are likely, having a judge oversee the process can prevent abuse and ensure fair treatment.
          </p>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-information-line mr-2 text-amber-500"></i>
              Important Consideration
            </h4>
            <p className="text-gray-700">
              Even with a trust, you still need a &quot;pour-over will&quot; to catch any assets that weren&apos;t transferred to the trust during your lifetime. So you&apos;re not choosing between a trust OR a will — with a trust, you need both.
            </p>
          </div>
        </section>

        {/* Cost Comparison Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Cost Comparison: Trust vs. Probate in Texas
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Let&apos;s break down the actual costs El Paso homeowners can expect with each approach:
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=calculator%20with%20financial%20documents%20showing%20estate%20planning%20costs%20comparison%20charts%20and%20graphs%20on%20modern%20desk%20professional%20business%20photography%20warm%20lighting%20clean%20organized%20workspace&width=800&height=500&seq=trust-probate-costs-004&orientation=landscape"
            alt="Cost comparison for estate planning options"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="px-6 py-4 text-left font-semibold">Cost Category</th>
                  <th className="px-6 py-4 text-left font-semibold">Living Trust</th>
                  <th className="px-6 py-4 text-left font-semibold">Will + Probate</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 font-medium">Initial Setup</td>
                  <td className="px-6 py-4">$2,000 - $5,000+</td>
                  <td className="px-6 py-4">$300 - $1,000</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 font-medium">Court Filing Fees</td>
                  <td className="px-6 py-4">$0 (no probate)</td>
                  <td className="px-6 py-4">$300 - $500</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 font-medium">Attorney Fees at Death</td>
                  <td className="px-6 py-4">$500 - $2,000 (trust administration)</td>
                  <td className="px-6 py-4">$2,000 - $10,000+ (probate)</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 font-medium">Executor/Trustee Fees</td>
                  <td className="px-6 py-4">Often waived (family member)</td>
                  <td className="px-6 py-4">Up to 5% of estate value</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 font-medium">Bond Premium</td>
                  <td className="px-6 py-4">$0</td>
                  <td className="px-6 py-4">$0 - $1,000+ (if required)</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 font-medium">Ongoing Maintenance</td>
                  <td className="px-6 py-4">Periodic updates ($200-$500)</td>
                  <td className="px-6 py-4">Minimal</td>
                </tr>
                <tr className="bg-teal-50">
                  <td className="px-6 py-4 font-bold">Typical Total (Moderate Estate)</td>
                  <td className="px-6 py-4 font-bold text-teal-600">$3,000 - $7,000</td>
                  <td className="px-6 py-4 font-bold">$3,000 - $12,000+</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            As you can see, the total costs can be similar for moderate estates. The key difference is <strong>when</strong> you pay: trusts require more upfront investment, while wills defer costs until after death. For larger estates or those with complications, trusts typically save money overall.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you&apos;re dealing with a property that has{' '}
            <a
              href="/elpaso-tx/owing-too-much-property-tax"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              property tax issues
            </a>{' '}
            or other complications, the costs of probate can escalate quickly, making a trust more attractive.
          </p>
        </section>

        {/* Middle Lead Capture Form */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h2 className="text-3xl font-bold mb-4">
                  Inherited Property in a Trust or Probate?
                </h2>
                <p className="text-gray-300 mb-6">
                  Whether your inherited property is in a trust or going through probate, we can help you sell quickly for a fair cash price. No repairs, no commissions, no hassle.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Buy properties from trusts</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Purchase probate properties</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Work with multiple heirs</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Close on your timeline</span>
                  </li>
                </ul>
              </div>
              <div>
                <TrustProbateForm
                  formId="middle-trust-probate-form"
                  title="Get Your Cash Offer"
                  subtitle="No obligation, no pressure"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Timeline Comparison Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Timeline Comparison: How Long Does Each Process Take?
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Time is often as important as money when settling an estate. Here&apos;s what El Paso families can typically expect:
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-teal-50 p-6 rounded-xl border border-teal-100">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-600 rounded-full mb-4">
                <i className="ri-time-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Trust Administration</h3>
              <p className="text-gray-700 mb-4">
                <strong>Typical Timeline: 2-6 months</strong>
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-0.5"></i>
                  <span>Successor trustee can act immediately</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-0.5"></i>
                  <span>No court approval needed for distributions</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-0.5"></i>
                  <span>Real estate can be sold quickly</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-0.5"></i>
                  <span>Beneficiaries receive assets faster</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <div className="w-14 h-14 flex items-center justify-center bg-gray-600 rounded-full mb-4">
                <i className="ri-scales-3-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Probate Process</h3>
              <p className="text-gray-700 mb-4">
                <strong>Typical Timeline: 6-18 months</strong>
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-gray-600 mr-2 mt-0.5"></i>
                  <span>Must wait for court to appoint executor</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-gray-600 mr-2 mt-0.5"></i>
                  <span>Creditor claim period (4+ months)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-gray-600 mr-2 mt-0.5"></i>
                  <span>Court approval may be needed for sales</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-gray-600 mr-2 mt-0.5"></i>
                  <span>Contested estates can take years</span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For families who need to sell inherited property quickly — perhaps to pay estate debts, divide assets among heirs, or simply move on — the speed advantage of a trust can be significant. If you&apos;re facing a situation where you need to{' '}
            <a
              href="/avoid-foreclosure"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              avoid foreclosure on inherited property
            </a>, the delays of probate can be particularly problematic.
          </p>
        </section>

        {/* Real Estate Considerations Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Real Estate Considerations for El Paso Homeowners
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=beautiful%20southwestern%20adobe%20style%20home%20in%20el%20paso%20texas%20with%20franklin%20mountains%20in%20background%20real%20estate%20for%20sale%20sign%20showing%20property%20transfer%20and%20estate%20planning%20concept%20warm%20golden%20hour%20lighting%20professional%20photography&width=800&height=500&seq=trust-probate-realestate-005&orientation=landscape"
            alt="El Paso home with estate planning considerations"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For most El Paso families, the family home is the largest asset in their estate. How you plan for this asset has significant implications:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Selling Property Held in a Trust
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            When real estate is properly titled in a trust, the successor trustee can sell it immediately after the grantor&apos;s death. There&apos;s no need to wait for probate, no court approval required, and no delays. The trustee simply signs the deed as &quot;Trustee of the [Name] Trust&quot; and the sale proceeds.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            This is particularly valuable when:
          </p>

          <ul className="space-y-3 mb-8">
            <li className="flex items-start">
              <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Multiple heirs need to divide the proceeds</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">The property needs repairs the estate can&apos;t afford</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Estate debts need to be paid quickly</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
              <span className="text-gray-700">Heirs live out of state and can&apos;t manage the property</span>
            </li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Selling Property Through Probate
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Selling real estate during probate is more complicated. The executor must first be appointed by the court, which can take weeks or months. Depending on the type of administration, the executor may need court approval before selling, which adds more time and expense.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            During this waiting period, the property still incurs costs: property taxes, insurance, utilities, and maintenance. If the estate doesn&apos;t have liquid assets to cover these expenses, heirs may need to pay out of pocket — or the property may deteriorate.
          </p>

          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-home-gear-line mr-2 text-teal-600"></i>
              Working With Cash Buyers
            </h4>
            <p className="text-gray-700">
              Whether property is in a trust or probate, cash buyers like TREX RE LLC can often provide solutions that traditional buyers can&apos;t. We understand the legal requirements of both processes and can work with trustees, executors, and attorneys to facilitate quick, hassle-free sales. If you&apos;re dealing with{' '}
              <a href="/elpaso-tx/sell-hoarder-houses" className="text-teal-600 hover:text-teal-700">
                hoarder houses
              </a>{' '}
              or properties needing significant repairs, we buy as-is.
            </p>
          </div>
        </section>

        {/* Which Is Right for You Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Which Is Right for You? A Decision Framework
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            After weighing all the factors, how do you decide? Here&apos;s a framework to help El Paso homeowners make the right choice:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            A Trust May Be Better If You:
          </h3>

          <div className="bg-teal-50 p-6 rounded-lg mb-6">
            <ul className="space-y-3">
              <li className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Own real estate worth $200,000 or more</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Own property in multiple states</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Value privacy and want to keep your affairs confidential</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Have a blended family or complex family dynamics</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Want to provide for minor children or special needs beneficiaries</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Are concerned about potential incapacity</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Want beneficiaries to receive assets quickly</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Have the budget for proper trust creation and funding</span>
              </li>
            </ul>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            A Will May Be Sufficient If You:
          </h3>

          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <ul className="space-y-3">
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-gray-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Have a modest estate with straightforward distribution wishes</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-gray-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Only own property in Texas</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-gray-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Have a simple family situation (spouse and/or children from one marriage)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-gray-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Are young and healthy with decades before estate planning becomes urgent</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-gray-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Have cooperative family members unlikely to contest the estate</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-gray-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Prefer lower upfront costs</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-gray-600 mr-3 mt-1"></i>
                <span className="text-gray-700">Most assets already have beneficiary designations (retirement accounts, life insurance)</span>
              </li>
            </ul>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-error-warning-line mr-2 text-amber-500"></i>
              Don&apos;t Forget: You Need Both
            </h4>
            <p className="text-gray-700">
              Even if you create a trust, you still need a pour-over will to catch any assets not transferred to the trust. And regardless of which route you choose, you should also have powers of attorney for healthcare and finances, and possibly a living will/advance directive. Estate planning is about more than just property distribution.
            </p>
          </div>
        </section>

        {/* Common Mistakes Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Common Mistakes to Avoid
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=person%20reviewing%20legal%20documents%20with%20concerned%20expression%20highlighting%20common%20estate%20planning%20mistakes%20warning%20signs%20professional%20photography%20warm%20lighting%20office%20setting%20with%20legal%20paperwork&width=800&height=500&seq=trust-probate-mistakes-006&orientation=landscape"
            alt="Avoiding common estate planning mistakes"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Whether you choose a trust or rely on a will, avoid these common pitfalls:
          </p>

          <div className="space-y-6 mb-8">
            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-close-circle-line text-red-500 mr-2"></i>
                Mistake #1: Creating a Trust But Not Funding It
              </h4>
              <p className="text-gray-700 text-sm">
                A trust only avoids probate for assets actually transferred into it. If you create a trust but leave your house titled in your personal name, that house still goes through probate. Work with your attorney to ensure all appropriate assets are properly retitled.
              </p>
            </div>
            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-close-circle-line text-red-500 mr-2"></i>
                Mistake #2: Forgetting to Update Beneficiary Designations
              </h4>
              <p className="text-gray-700 text-sm">
                Retirement accounts, life insurance, and other assets with beneficiary designations pass outside of both wills and trusts. If these designations are outdated (naming an ex-spouse, for example), your estate plan won&apos;t work as intended.
              </p>
            </div>
            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-close-circle-line text-red-500 mr-2"></i>
                Mistake #3: DIY Estate Planning for Complex Situations
              </h4>
              <p className="text-gray-700 text-sm">
                Online will services can work for simple situations, but complex estates, blended families, or significant assets require professional guidance. The cost of fixing a poorly drafted document far exceeds the cost of doing it right the first time.
              </p>
            </div>
            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-close-circle-line text-red-500 mr-2"></i>
                Mistake #4: Not Reviewing and Updating Your Plan
              </h4>
              <p className="text-gray-700 text-sm">
                Life changes — marriages, divorces, births, deaths, moves, and major purchases all affect your estate plan. Review your documents every 3-5 years or after any major life event.
              </p>
            </div>
            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-close-circle-line text-red-500 mr-2"></i>
                Mistake #5: Not Telling Anyone About Your Plan
              </h4>
              <p className="text-gray-700 text-sm">
                Your estate plan only works if people know about it. Make sure your successor trustee, executor, and key family members know where to find your documents and understand your wishes.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I create a trust myself, or do I need an attorney?
              </h3>
              <p className="text-gray-700">
                While DIY trust kits exist, we strongly recommend working with an experienced estate planning attorney. Trusts involve complex legal and tax considerations, and mistakes can be costly. An improperly drafted trust may not achieve your goals or could even create additional problems. For most El Paso homeowners, the cost of professional guidance is well worth the peace of mind.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Does a trust protect assets from creditors or lawsuits?
              </h3>
              <p className="text-gray-700">
                A standard revocable living trust does NOT protect assets from your creditors during your lifetime — you still control the assets, so creditors can still reach them. However, trusts can protect assets for your beneficiaries after your death, depending on how they&apos;re structured. Asset protection trusts exist but are more complex and expensive.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What happens if I have a trust but also a will that says something different?
              </h3>
              <p className="text-gray-700">
                Assets in the trust are distributed according to the trust terms, regardless of what your will says. Your will only controls assets that pass through probate — which, if your trust is properly funded, should be minimal. This is why coordination between your trust and will is essential.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I sell my house if it&apos;s in a trust?
              </h3>
              <p className="text-gray-700">
                Yes! As the trustee of your own revocable living trust, you can sell, refinance, or do anything else with trust property just as you could if it were in your personal name. The trust doesn&apos;t restrict your control during your lifetime.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How do I transfer my El Paso home into a trust?
              </h3>
              <p className="text-gray-700">
                Transferring real estate to a trust requires a new deed — typically a warranty deed or special warranty deed — that transfers ownership from you individually to you as trustee of your trust. This deed must be recorded with the El Paso County Clerk. Your attorney should handle this as part of the trust creation process.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Will putting my house in a trust affect my property taxes or homestead exemption?
              </h3>
              <p className="text-gray-700">
                In Texas, transferring your home to a revocable living trust should not affect your homestead exemption or trigger a property tax reassessment, as long as you remain the beneficiary of the trust during your lifetime. However, it&apos;s wise to confirm with the{' '}
                <a href="/elpaso-tx/epcad" className="text-teal-600 hover:text-teal-700">
                  El Paso Central Appraisal District (EPCAD)
                </a>{' '}
                that your exemption remains in place after the transfer.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What if family members contest the trust or will?
              </h3>
              <p className="text-gray-700">
                Both trusts and wills can be contested, though trusts are generally harder to challenge because they don&apos;t go through the public probate process. Common grounds for contest include lack of mental capacity, undue influence, or improper execution. Working with an experienced attorney and following proper procedures reduces the risk of successful challenges.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white">
            <div className="text-center mb-8">
              <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
                <i className="ri-hand-heart-line text-4xl"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Need to Sell Property From a Trust or Probate Estate?
              </h2>
              <p className="text-xl text-teal-100 max-w-2xl mx-auto">
                Whether you&apos;re a trustee, executor, or heir, we can help you sell inherited property quickly and fairly. We understand the legal requirements and work with your attorney to ensure a smooth transaction.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8">
              <TrustProbateForm
                formId="final-trust-probate-form"
                title="Get Your Free Cash Offer"
                subtitle="No obligation, compassionate service"
              />
            </div>

            <div className="mt-8 text-center">
              <p className="text-teal-100 mb-4">Prefer to talk? Call us directly:</p>
              <a
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-lg"
              >
                <i className="ri-phone-line mr-2"></i>(915) 206-5814
              </a>
            </div>
          </div>
        </section>

        {/* Related Resources */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <a
              href="/elpaso-tx/texas-probate-guide"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-scales-3-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Texas Probate Guide</p>
                <p className="text-sm text-gray-600">Complete probate process explained</p>
              </div>
            </a>
            <a
              href="/elpaso-tx/texas-intestacy-laws"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-file-list-3-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Texas Intestacy Laws</p>
                <p className="text-sm text-gray-600">What happens without a will</p>
              </div>
            </a>
            <a
              href="/inherited-homes"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-heart-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Selling Inherited Homes</p>
                <p className="text-sm text-gray-600">Options for inherited property</p>
              </div>
            </a>
            <a
              href="/get-cash-offer-for-my-property"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-money-dollar-circle-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Get Cash Offer</p>
                <p className="text-sm text-gray-600">Request your no-obligation offer</p>
              </div>
            </a>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
}
